import React, { useState, useEffect, useContext, useMemo } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { FaBoxes, FaExclamationTriangle } from "react-icons/fa";
import { DataGridTable } from "../components";
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import "./StocksPharmacie.css";

export default function StocksPharmacie() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // États pour les filtres
  const [produit, setProduit] = useState('');
  const [dateExpiration, setDateExpiration] = useState('');
  const [alertType, setAlertType] = useState(''); // 'stock_faible' ou 'expires_bientot'


  useEffect(() => {
    const fetchStocks = async () => {
      try {
        let endpoint = "/api/stocks-pharmacie/";
        const params = new URLSearchParams();
        
        if (alertType === 'stock_faible') {
          endpoint = "/api/stocks-pharmacie/stock-faible/";
          params.append('seuil', '10'); // Seuil par défaut
        } else if (alertType === 'expires_bientot') {
          endpoint = "/api/stocks-pharmacie/expires-bientot/";
          params.append('jours', '30'); // 30 jours par défaut
        } else {
          if (produit) params.append('produit', produit);
          if (dateExpiration) params.append('date_expiration', dateExpiration);
        }

        const response = await api.get(endpoint, { params });
        setStocks(response.data);
        setLoading(false);
      } catch (err) {
        setError(t.errorLoadingStocks);
        setLoading(false);
      }
    };
    fetchStocks();
  }, [produit, dateExpiration, alertType]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const getDaysUntilExpiration = (dateString) => {
    if (!dateString) return null;
    const expiration = new Date(dateString);
    const today = new Date();
    const diffTime = expiration - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };


  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "produit_nom",
        headerName: t.product,
        flex: 1,
        minWidth: 200,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: "produit_type",
        headerName: t.productType,
        width: 130,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: "quantite",
        headerName: t.quantity,
        width: 120,
        headerAlign: 'right',
        align: 'right',
      },
      {
        field: "produit_unite",
        headerName: t.unit,
        width: 100,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: "date_expiration",
        headerName: t.expirationDate,
        width: 160,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => formatDate(value),
        renderCell: (params) => {
          const daysLeft = params.row.jours_avant_expiration;
          if (params.row.est_expire) {
            return <span className="expired-badge">{t.expired}</span>;
          } else if (daysLeft !== undefined && daysLeft !== null) {
            if (daysLeft <= 30) {
              return <span className="expiring-soon-badge">{formatDate(params.value)} ({daysLeft} {t.days})</span>;
            }
          }
          return formatDate(params.value);
        },
      },
      {
        field: "numero_lot",
        headerName: t.lotNumber,
        width: 150,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (value) => value || "N/A",
      },
    ];

    return [...baseColumns];
  }, [t]);

  return (
    <div className="stocks-pharmacie-page">
      <div className="title-badge">
        <FaBoxes className="page-title-icon" aria-hidden="true" />
        <span>{t.pharmacyStock}</span>
      </div>
      {loading && <p>{t.loading}</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <div className="stocks-pharmacie-container">
          {/* Alertes */}
          <div className="alerts-section">
            <button
              className={`alert-button ${alertType === 'stock_faible' ? 'active' : ''}`}
              onClick={() => setAlertType(alertType === 'stock_faible' ? '' : 'stock_faible')}
            >
              <FaExclamationTriangle />
              {t.lowStock}
            </button>
            <button
              className={`alert-button ${alertType === 'expires_bientot' ? 'active' : ''}`}
              onClick={() => setAlertType(alertType === 'expires_bientot' ? '' : 'expires_bientot')}
            >
              <FaExclamationTriangle />
              {t.expiringSoonAlert}
            </button>
            {alertType && (
              <button
                className="clear-alert-button"
                onClick={() => setAlertType('')}
              >
                {t.showAll}
              </button>
            )}
          </div>

          {!alertType && (
            <>
              <h2 className="titleFiltre">{t.filters}</h2>
              <div className="filtres">
                <input
                  type="text"
                  className="filtre-produit"
                  placeholder={t.productId}
                  value={produit}
                  onChange={(e) => setProduit(e.target.value)}
                />
                <input
                  type="date"
                  className="filtre-date"
                  value={dateExpiration}
                  onChange={(e) => setDateExpiration(e.target.value)}
                  placeholder={t.expirationDate}
                />
              </div>
            </>
          )}

          {/* DataGrid */}
          <DataGridTable
            rows={stocks}
            columns={columns}
            loading={loading}
            storageKey="stocks-pharmacie-zoom-level"
            initialSort={{ field: 'date_expiration', sort: 'asc' }}
            pageSize={25}
            containerClassName="stocks-grid-container"
          />
        </div>
      )}
    </div>
  );
}